import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Clock, AlertTriangle, ShieldCheck, FileWarning,
  Upload, ArrowRight, ChevronDown, ChevronUp, Calendar,
} from "lucide-react";
import api from "@/lib/api";

/**
 * CaseReadinessCard — unified "how ready is this case to file?" widget.
 *
 * Combines three signals from `GET /api/cases/{cid}/readiness`:
 *   1. Live progress (% done + estimated minutes remaining)
 *   2. Smart document checklist (what's missing per person)
 *   3. Health score (0-100, A-F grade) with actionable issues
 *
 * `compact` mode renders a slim strip for the Dashboard card. `expanded`
 * mode is the full 3-panel card used inside CaseWorkspace.
 */

const GRADE_COLORS = {
  A: { bg: "#16A34A", text: "#FFFFFF", chip: "#DCFCE7", chipText: "#166534" },
  B: { bg: "#16A34A", text: "#FFFFFF", chip: "#DCFCE7", chipText: "#166534" },
  C: { bg: "#F59E0B", text: "#FFFFFF", chip: "#FEF3C7", chipText: "#78350F" },
  D: { bg: "#EA580C", text: "#FFFFFF", chip: "#FED7AA", chipText: "#7C2D12" },
  F: { bg: "#DC2626", text: "#FFFFFF", chip: "#FEE2E2", chipText: "#7F1D1D" },
};

const SEVERITY_ICON = {
  high: { Icon: AlertTriangle, color: "#DC2626" },
  medium: { Icon: FileWarning, color: "#F59E0B" },
  low: { Icon: Clock, color: "#64748B" },
};

export function useCaseReadiness(caseId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/cases/${caseId}/readiness`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caseId]);

  return { readiness: data, loading };
}

export function useCaseVisaStatus(caseId) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    api.get(`/cases/${caseId}/visa-status`)
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, [caseId]);
  return status;
}

/** Compact horizontal strip — used on the Dashboard case card. */
export function ReadinessStrip({ readiness, visaStatus }) {
  if (!readiness) return null;
  const pct = readiness.progress?.percent ?? 0;
  const mins = readiness.progress?.estimated_minutes_remaining ?? 0;
  const missing = readiness.docs_checklist?.total_missing ?? 0;
  const grade = readiness.health?.grade || "C";
  const g = GRADE_COLORS[grade] || GRADE_COLORS.C;

  // Visa Bulletin chip — color-coded by fad_status
  const visa = visaStatus;
  const visaChip = (() => {
    if (!visa || (!visa.tracked && !visa.is_immediate_relative)) return null;
    if (visa.is_immediate_relative) {
      return { text: "IR · Current", bg: "#DCFCE7", color: "#166534",
               title: "Immediate relative — no visa-number wait" };
    }
    if (visa.fad_status === "current") {
      return {
        text: `${visa.category || "—"} · CURRENT`,
        bg: "#DCFCE7", color: "#166534",
        title: `Priority date current under ${visa.category} (${visa.country})`,
      };
    }
    if (visa.fad_status === "unavailable") {
      return {
        text: `${visa.category || "—"} · UNAVAILABLE`,
        bg: "#FEE2E2", color: "#7F1D1D",
        title: `${visa.category} (${visa.country}) unavailable this month`,
      };
    }
    if (visa.fad_status === "backlogged" && visa.estimated_wait_months) {
      const m = visa.estimated_wait_months;
      const short = m >= 24 ? `~${Math.round(m/12)}y behind` : `~${m}mo behind`;
      return {
        text: `${visa.category || "—"} · ${short}`,
        bg: "#FFFBEB", color: "#B45309",
        title: `Priority date ${visa.priority_date} — cutoff ${visa.fad_cutoff}`,
      };
    }
    return null;
  })();

  return (
    <div
      className="mt-3 flex items-center flex-wrap gap-2 text-[11px] font-semibold"
      data-testid="readiness-strip"
    >
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
        style={{ background: g.chip, color: g.chipText }}
        title={`Case health: ${readiness.health?.score}/100`}
      >
        <ShieldCheck className="w-3 h-3" /> Health {grade}
      </span>
      {visaChip && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: visaChip.bg, color: visaChip.color }}
          title={visaChip.title}
          data-testid="readiness-visa-chip"
        >
          <Calendar className="w-3 h-3" /> {visaChip.text}
        </span>
      )}
      {mins > 0 && pct < 100 && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: "#EFF6FF", color: "#1E40AF" }}
        >
          <Clock className="w-3 h-3" /> ~{mins} min left
        </span>
      )}
      {missing > 0 && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: "#FEF3C7", color: "#78350F" }}
        >
          <Upload className="w-3 h-3" /> {missing} doc{missing > 1 ? "s" : ""} needed
        </span>
      )}
    </div>
  );
}

/** Full 3-panel card — used at the top of CaseWorkspace. */
export function CaseReadinessCard({ caseId }) {
  const { readiness, loading } = useCaseReadiness(caseId);
  const [openHealth, setOpenHealth] = useState(false);
  const [openDocs, setOpenDocs] = useState(true);

  if (loading || !readiness) return null;

  const { progress, docs_checklist, health, is_filing_ready } = readiness;
  const grade = health?.grade || "C";
  const g = GRADE_COLORS[grade] || GRADE_COLORS.C;
  const issues = health?.issues || [];
  const perPerson = docs_checklist?.per_person || [];
  const personsMissingDocs = perPerson.filter((p) => p.docs_missing.length > 0);

  return (
    <div
      className="card-surface p-5 sm:p-6 mb-6"
      style={{ background: "#FFFFFF" }}
      data-testid="case-readiness-card"
    >
      {/* Header row: filing-ready badge OR grade + progress bar + mins */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-md flex flex-col items-center justify-center flex-shrink-0"
            style={{ background: g.bg, color: g.text }}
            data-testid="readiness-grade-badge"
          >
            <div className="font-display font-extrabold text-xl leading-none">{grade}</div>
            <div className="text-[9px] uppercase tracking-wider leading-none mt-0.5">Health</div>
          </div>
          <div>
            <div className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
              {is_filing_ready ? "Ready to file!" : "Case in progress"}
            </div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {progress.percent}% complete
              {progress.estimated_minutes_remaining > 0 && progress.percent < 100 && (
                <> · ~{progress.estimated_minutes_remaining} min remaining</>
              )}
              {is_filing_ready && <> · No blockers</>}
            </div>
          </div>
        </div>
        {is_filing_ready ? (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold"
            style={{ background: "#DCFCE7", color: "#166534" }}
          >
            <CheckCircle2 className="w-4 h-4" /> All checks passed
          </div>
        ) : null}
      </div>

      {/* Progress bar */}
      <div
        className="h-2.5 rounded-full overflow-hidden mb-1"
        style={{ background: "var(--border-stone)" }}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${progress.percent}%`, background: "var(--action)" }}
          data-testid="readiness-progress-bar"
        />
      </div>
      <div className="flex items-center justify-between text-[11px] mb-4" style={{ color: "var(--text-secondary)" }}>
        <span>{progress.missing_field_count} fields left · {progress.missing_doc_count} docs left</span>
        <span>Auto-saved as you go</span>
      </div>

      {/* Documents needed — collapsible */}
      {docs_checklist?.total_missing > 0 && (
        <div className="mt-3 border rounded-md" style={{ borderColor: "var(--border-stone)" }}>
          <button
            type="button"
            onClick={() => setOpenDocs((v) => !v)}
            className="w-full flex items-center justify-between p-3 text-left"
            data-testid="readiness-docs-toggle"
          >
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" style={{ color: "#B45309" }} />
              <span className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                Documents still needed ({docs_checklist.total_missing})
              </span>
            </div>
            {openDocs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {openDocs && (
            <div className="px-3 pb-3 space-y-2">
              {personsMissingDocs.map((p) => (
                <div
                  key={p.person_ref}
                  className="p-2.5 rounded"
                  style={{ background: "#FFF7ED" }}
                  data-testid={`readiness-person-${p.person_ref}`}
                >
                  <div className="text-xs font-bold mb-1" style={{ color: "#7C2D12" }}>
                    {p.display_name}
                  </div>
                  <ul className="text-xs space-y-1" style={{ color: "#78350F" }}>
                    {p.docs_missing.map((d) => (
                      <li key={d.id} className="flex items-start gap-1.5">
                        <Upload className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{d.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <Link
                to={`/case/${caseId}#roster`}
                className="inline-flex items-center gap-1 text-xs font-semibold mt-1"
                style={{ color: "var(--action)" }}
                data-testid="readiness-upload-link"
              >
                Upload documents <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Health issues — collapsible */}
      {issues.length > 0 && (
        <div className="mt-3 border rounded-md" style={{ borderColor: "var(--border-stone)" }}>
          <button
            type="button"
            onClick={() => setOpenHealth((v) => !v)}
            className="w-full flex items-center justify-between p-3 text-left"
            data-testid="readiness-health-toggle"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" style={{ color: g.bg }} />
              <span className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                Case health · {health.score}/100 ({grade})
              </span>
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                — {issues.length} item{issues.length > 1 ? "s" : ""} to review
              </span>
            </div>
            {openHealth ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {openHealth && (
            <div className="px-3 pb-3 space-y-2">
              {issues.map((issue) => {
                const { Icon, color } = SEVERITY_ICON[issue.severity] || SEVERITY_ICON.low;
                return (
                  <div
                    key={`${issue.kind}-${issue.message}`}
                    className="flex items-start gap-2 p-2 rounded text-xs"
                    style={{ background: "#F8FAFC" }}
                    data-testid={`readiness-issue-${issue.kind}`}
                  >
                    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color }} />
                    <div>
                      <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
                        {issue.message}
                      </div>
                      <div className="mt-0.5" style={{ color: "var(--text-secondary)" }}>
                        {issue.action}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
